import React, { useState, useEffect } from 'react';
import gisService from '@/modules/utility/gis/services/gisService';
import { useSnackbar } from 'notistack';
import { ListSkeleton } from '@/components/base/Skeleton';

interface DataSource {
  id: number;
  source_code: string;
  source_name: string;
  source_name_local?: string;
  source_type: string;
  source_category?: string;
  connection_config: any;
  capabilities?: any;
  supported_srid?: any;
  default_srid?: number;
  bounding_box?: any;
  health_status?: string;
  last_check_timestamp?: string;
  last_error_message?: string;
  response_time_ms?: number;
  timeout_seconds?: number;
  max_retry_attempts?: number;
  cache_enabled?: boolean;
  cache_ttl_seconds?: number;
  icon?: string;
  description?: string;
  display_order?: number;
  sequence_id?: number;
  is_public?: boolean;
  requires_authentication?: boolean;
  allowed_roles?: any;
  is_active?: boolean;
  metadata?: any;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
  type_id?: number;
  type_code?: string;
  type_name?: string;
  type_name_local?: string;
  type_icon?: string;
  type_icon_image?: string;
  type_color?: string;
  type_category?: string;
}

interface DataSourceType {
  id: number;
  type_code: string;
  type_name: string;
  type_name_local?: string;
  icon?: string;
  icon_image?: string;
  color?: string;
  description?: string;
  category?: string;
  requires_connection?: boolean;
  supports_layers?: boolean;
  is_realtime?: boolean;
  display_order?: number;
  sequence_id?: number;
  is_active?: boolean;
}

const DataStores: React.FC = () => {
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [dataSourceTypes, setDataSourceTypes] = useState<DataSourceType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<string>('source_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDataSourceType, setSelectedDataSourceType] = useState<DataSourceType | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingDataSource, setEditingDataSource] = useState<DataSource | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [showPasswords, setShowPasswords] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const itemsPerPage = 10;

  // Form state
  const [formData, setFormData] = useState({
    source_code: '',
    source_name: '',
    source_name_local: '',
    source_type: '',
    source_category: '',
    description: '',
    type_id: '',
    connection_config: {},
    is_active: true,
    is_public: false,
    requires_authentication: false,
    default_srid: 4326,
    timeout_seconds: 30,
    max_retry_attempts: 3,
    cache_enabled: false,
    cache_ttl_seconds: 3600,
  });

  // Load data sources and types
  useEffect(() => {
    loadDataSources();
    loadDataSourceTypes();
  }, []);

  const loadDataSources = async () => {
    try {
      setIsLoading(true);
      const response = await gisService.getAllDataSources();

      if (response.status === 200 && response.data) {
        setDataSources(response.data);
      }
    } catch (error) {
      console.error('Failed to load data sources:', error);
      enqueueSnackbar('Failed to load data sources', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadDataSourceTypes = async () => {
    try {
      const response = await gisService.getDataSourceTypes();

      if (response.status === 200 && response.data) {
        setDataSourceTypes(response.data);
      }
    } catch (error) {
      console.error('Failed to load data source types:', error);
      enqueueSnackbar('Failed to load data source types', { variant: 'error' });
    }
  };

  const filteredDataSources = dataSources.filter(source => {
    const matchesSearch = source.source_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         source.source_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         source.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || source.source_category === selectedCategory || source.type_category === selectedCategory;
    const matchesType = selectedTypeFilter === null || source.type_id === selectedTypeFilter;
    return matchesSearch && matchesCategory && matchesType;
  });

  const sortedDataSources = [...filteredDataSources].sort((a: any, b: any) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const paginatedDataSources = sortedDataSources.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedDataSources.length / itemsPerPage);

  const getCategoryCount = (category: string) => {
    return dataSources.filter(source => source.source_category === category || source.type_category === category).length;
  };

  // Get detailed category statistics with type breakdowns
  const getCategoryStats = (category: string) => {
    const categorySources = dataSources.filter(
      source => source.source_category === category || source.type_category === category
    );

    // Get all types for this category from dataSourceTypes
    const categoryTypes = dataSourceTypes.filter(
      type => type.category === category
    );

    const typeBreakdown: { [key: string]: {
      total: number;
      active: number;
      inactive: number;
      typeId: number;
    } } = {};

    // Initialize all types with 0 counts
    categoryTypes.forEach(type => {
      typeBreakdown[type.type_name] = {
        total: 0,
        active: 0,
        inactive: 0,
        typeId: type.id
      };
    });

    // Count actual sources for each type
    categorySources.forEach(source => {
      const typeName = source.type_name || source.source_type || 'Unknown';
      if (!typeBreakdown[typeName]) {
        typeBreakdown[typeName] = {
          total: 0,
          active: 0,
          inactive: 0,
          typeId: source.type_id || 0
        };
      }
      typeBreakdown[typeName].total++;
      if (source.is_active) {
        typeBreakdown[typeName].active++;
      } else {
        typeBreakdown[typeName].inactive++;
      }
    });

    return {
      total: categorySources.length,
      active: categorySources.filter(s => s.is_active).length,
      inactive: categorySources.filter(s => !s.is_active).length,
      types: typeBreakdown
    };
  };

  const getHealthStatusColor = (status?: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'degraded': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'down': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await gisService.deleteDataSource(id);
      enqueueSnackbar('Data source deleted successfully', { variant: 'success' });
      loadDataSources();
      setShowDeleteModal(null);
    } catch (error) {
      console.error('Failed to delete data source:', error);
      enqueueSnackbar('Failed to delete data source', { variant: 'error' });
    }
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return 'ri-expand-up-down-line';
    return sortOrder === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line';
  };

  const handleDataSourceTypeSelect = (dataSourceType: DataSourceType) => {
    setSelectedDataSourceType(dataSourceType);
    setFormData({
      ...formData,
      type_id: dataSourceType.id.toString(),
      source_type: dataSourceType.type_code,
      source_category: dataSourceType.category || '',
    });
  };

  const resetForm = () => {
    setFormData({
      source_code: '',
      source_name: '',
      source_name_local: '',
      source_type: '',
      source_category: '',
      description: '',
      type_id: '',
      connection_config: {},
      is_active: true,
      is_public: false,
      requires_authentication: false,
      default_srid: 4326,
      timeout_seconds: 30,
      max_retry_attempts: 3,
      cache_enabled: false,
      cache_ttl_seconds: 3600,
    });
    setSelectedDataSourceType(null);
    setTestResult(null);
    setShowPasswords(false);
  };

  const handleEdit = (source: DataSource) => {
    setEditingDataSource(source);
    setFormData({
      source_code: source.source_code,
      source_name: source.source_name,
      source_name_local: source.source_name_local || '',
      source_type: source.source_type,
      source_category: source.source_category || '',
      description: source.description || '',
      type_id: source.type_id?.toString() || '',
      connection_config: source.connection_config || {},
      is_active: source.is_active !== false,
      is_public: source.is_public || false,
      requires_authentication: source.requires_authentication || false,
      default_srid: source.default_srid || 4326,
      timeout_seconds: source.timeout_seconds || 30,
      max_retry_attempts: source.max_retry_attempts || 3,
      cache_enabled: source.cache_enabled || false,
      cache_ttl_seconds: source.cache_ttl_seconds || 3600,
    });
    const type = dataSourceTypes.find(t => t.id === source.type_id);
    if (type) {
      setSelectedDataSourceType(type);
    }
    setTestResult(null);
    setShowPasswords(false);
    setShowEditModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const dataToSubmit = {
        ...formData,
        type_id: parseInt(formData.type_id),
      };

      if (editingDataSource) {
        await gisService.updateDataSource(editingDataSource.id, dataToSubmit);
        enqueueSnackbar('Data source updated successfully', { variant: 'success' });
      } else {
        await gisService.createDataSource(dataToSubmit);
        enqueueSnackbar('Data source created successfully', { variant: 'success' });
      }

      loadDataSources();
      setShowCreateModal(false);
      setShowEditModal(false);
      resetForm();
      setEditingDataSource(null);
    } catch (error) {
      console.error('Failed to save data source:', error);
      enqueueSnackbar('Failed to save data source', { variant: 'error' });
    }
  };

  // Utility function to mask passwords in config
  const maskPasswords = (config: any): any => {
    if (!config || typeof config !== 'object') return config;

    const masked = { ...config };
    const passwordKeys = ['password', 'passwd', 'pwd', 'secret', 'api_key', 'apiKey', 'token'];

    Object.keys(masked).forEach(key => {
      if (passwordKeys.some(pk => key.toLowerCase().includes(pk.toLowerCase()))) {
        if (masked[key] && typeof masked[key] === 'string') {
          masked[key] = '********';
        }
      }
    });

    return masked;
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setTestResult(null);
      const response = await gisService.testDataSourceConnection(formData);

      if (response.status === 200 && response.data) {
        const result = response.data;
        setTestResult(result);

        if (result.success) {
          enqueueSnackbar(`${result.message} (${result.response_time_ms}ms)`, { variant: 'success' });
        } else {
          enqueueSnackbar(result.message, { variant: 'error' });
        }
      }
    } catch (error: any) {
      console.error('Connection test failed:', error);
      const errorMsg = error?.response?.data?.error_msg || error?.message || 'Connection test failed';
      enqueueSnackbar(errorMsg, { variant: 'error' });
      setTestResult({
        success: false,
        message: errorMsg,
        response_time_ms: 0
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const getUniqueCategories = () => {
    const categories = new Set<string>();
    dataSources.forEach(source => {
      if (source.source_category) categories.add(source.source_category);
      if (source.type_category) categories.add(source.type_category);
    });
    return Array.from(categories);
  };

  if (isLoading) {
    return (
      <ListSkeleton />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">GIS Data Sources</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage data source connections and configure GIS data providers
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line mr-2"></i>
          New Data Source
        </button>
      </div>

      {/* Category Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Server Data Sources Card */}
        {(() => {
          const stats = getCategoryStats('server');
          return (
            <div
              className={`bg-white dark:bg-gray-800 p-5 rounded-lg border-2 transition-all ${
                selectedCategory === 'server' && selectedTypeFilter === null
                  ? 'border-blue-500 shadow-lg'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div
                onClick={() => {
                  setSelectedCategory('server');
                  setSelectedTypeFilter(null);
                }}
                className="flex items-center mb-3 cursor-pointer hover:opacity-80"
              >
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <i className="ri-server-line text-xl text-blue-600 dark:text-blue-400"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Server Data Sources</p>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{stats.total}</p>
                </div>
              </div>
              <div className="space-y-1 pl-1">
                {Object.entries(stats.types).map(([typeName, counts]) => (
                  <div
                    key={typeName}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCategory('server');
                      setSelectedTypeFilter(counts.typeId);
                    }}
                    className={`flex items-center justify-between text-xs px-2 py-1.5 rounded cursor-pointer transition-colors ${
                      selectedTypeFilter === counts.typeId
                        ? 'bg-blue-100 dark:bg-blue-900/30'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className={`${selectedTypeFilter === counts.typeId ? 'font-semibold text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'}`}>
                      {typeName}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-900 dark:text-white font-medium">{counts.total}</span>
                      {counts.total > 0 && (
                        <>
                          <span className="text-green-600 dark:text-green-400">({counts.active})</span>
                          {counts.inactive > 0 && (
                            <span className="text-red-600 dark:text-red-400">({counts.inactive})</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {Object.keys(stats.types).length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-500 italic px-2">No types defined</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Service Data Sources Card */}
        {(() => {
          const stats = getCategoryStats('service');
          return (
            <div
              className={`bg-white dark:bg-gray-800 p-5 rounded-lg border-2 transition-all ${
                selectedCategory === 'service' && selectedTypeFilter === null
                  ? 'border-green-500 shadow-lg'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div
                onClick={() => {
                  setSelectedCategory('service');
                  setSelectedTypeFilter(null);
                }}
                className="flex items-center mb-3 cursor-pointer hover:opacity-80"
              >
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                  <i className="ri-global-line text-xl text-green-600 dark:text-green-400"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Service Data Sources</p>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">{stats.total}</p>
                </div>
              </div>
              <div className="space-y-1 pl-1">
                {Object.entries(stats.types).map(([typeName, counts]) => (
                  <div
                    key={typeName}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCategory('service');
                      setSelectedTypeFilter(counts.typeId);
                    }}
                    className={`flex items-center justify-between text-xs px-2 py-1.5 rounded cursor-pointer transition-colors ${
                      selectedTypeFilter === counts.typeId
                        ? 'bg-green-100 dark:bg-green-900/30'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className={`${selectedTypeFilter === counts.typeId ? 'font-semibold text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-400'}`}>
                      {typeName}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-900 dark:text-white font-medium">{counts.total}</span>
                      {counts.total > 0 && (
                        <>
                          <span className="text-green-600 dark:text-green-400">({counts.active})</span>
                          {counts.inactive > 0 && (
                            <span className="text-red-600 dark:text-red-400">({counts.inactive})</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {Object.keys(stats.types).length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-500 italic px-2">No types defined</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* File Data Sources Card */}
        {(() => {
          const stats = getCategoryStats('file');
          return (
            <div
              className={`bg-white dark:bg-gray-800 p-5 rounded-lg border-2 transition-all ${
                selectedCategory === 'file' && selectedTypeFilter === null
                  ? 'border-purple-500 shadow-lg'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div
                onClick={() => {
                  setSelectedCategory('file');
                  setSelectedTypeFilter(null);
                }}
                className="flex items-center mb-3 cursor-pointer hover:opacity-80"
              >
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                  <i className="ri-folder-line text-xl text-purple-600 dark:text-purple-400"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">File Data Sources</p>
                  <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{stats.total}</p>
                </div>
              </div>
              <div className="space-y-1 pl-1">
                {Object.entries(stats.types).map(([typeName, counts]) => (
                  <div
                    key={typeName}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCategory('file');
                      setSelectedTypeFilter(counts.typeId);
                    }}
                    className={`flex items-center justify-between text-xs px-2 py-1.5 rounded cursor-pointer transition-colors ${
                      selectedTypeFilter === counts.typeId
                        ? 'bg-purple-100 dark:bg-purple-900/30'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className={`${selectedTypeFilter === counts.typeId ? 'font-semibold text-purple-700 dark:text-purple-300' : 'text-gray-600 dark:text-gray-400'}`}>
                      {typeName}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-900 dark:text-white font-medium">{counts.total}</span>
                      {counts.total > 0 && (
                        <>
                          <span className="text-green-600 dark:text-green-400">({counts.active})</span>
                          {counts.inactive > 0 && (
                            <span className="text-red-600 dark:text-red-400">({counts.inactive})</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {Object.keys(stats.types).length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-500 italic px-2">No types defined</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Cloud Data Sources Card */}
        {(() => {
          const stats = getCategoryStats('cloud');
          return (
            <div
              className={`bg-white dark:bg-gray-800 p-5 rounded-lg border-2 transition-all ${
                selectedCategory === 'cloud' && selectedTypeFilter === null
                  ? 'border-orange-500 shadow-lg'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div
                onClick={() => {
                  setSelectedCategory('cloud');
                  setSelectedTypeFilter(null);
                }}
                className="flex items-center mb-3 cursor-pointer hover:opacity-80"
              >
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                  <i className="ri-cloud-line text-xl text-orange-600 dark:text-orange-400"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Cloud Data Sources</p>
                  <p className="text-lg font-bold text-orange-600 dark:text-orange-400">{stats.total}</p>
                </div>
              </div>
              <div className="space-y-1 pl-1">
                {Object.entries(stats.types).map(([typeName, counts]) => (
                  <div
                    key={typeName}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCategory('cloud');
                      setSelectedTypeFilter(counts.typeId);
                    }}
                    className={`flex items-center justify-between text-xs px-2 py-1.5 rounded cursor-pointer transition-colors ${
                      selectedTypeFilter === counts.typeId
                        ? 'bg-orange-100 dark:bg-orange-900/30'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className={`${selectedTypeFilter === counts.typeId ? 'font-semibold text-orange-700 dark:text-orange-300' : 'text-gray-600 dark:text-gray-400'}`}>
                      {typeName}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-900 dark:text-white font-medium">{counts.total}</span>
                      {counts.total > 0 && (
                        <>
                          <span className="text-green-600 dark:text-green-400">({counts.active})</span>
                          {counts.inactive > 0 && (
                            <span className="text-red-600 dark:text-red-400">({counts.inactive})</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {Object.keys(stats.types).length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-500 italic px-2">No types defined</p>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search data sources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white w-full sm:w-64"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedCategory === 'all'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
              {getUniqueCategories().map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Showing {paginatedDataSources.length} of {sortedDataSources.length} sources
            </span>
          </div>
        </div>
      </div>

      {/* Data Sources Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('source_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    <i className={`${getSortIcon('source_name')} text-sm`}></i>
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('type_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    <i className={`${getSortIcon('type_name')} text-sm`}></i>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Health
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedDataSources.map((source) => (
                <tr key={source.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3" style={{
                        backgroundColor: source.type_color ? `${source.type_color}20` : '#e5e7eb'
                      }}>
                        <i className={`${source.type_icon || 'ri-database-line'}`} style={{
                          color: source.type_color || '#6b7280'
                        }}></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{source.source_name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{source.description || source.source_code}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {source.type_name || source.source_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      {source.type_category || source.source_category || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getHealthStatusColor(source.health_status)}`}>
                      {source.health_status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      source.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {source.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(source)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Edit"
                      >
                        <i className="ri-edit-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => setShowDeleteModal(source.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {paginatedDataSources.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                        <i className="ri-database-2-line text-3xl text-gray-400 dark:text-gray-500"></i>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No data sources found
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
                        {sortedDataSources.length === 0 && dataSources.length > 0
                          ? 'No data sources match your current filters. Try adjusting your search or category selection.'
                          : 'Get started by adding your first data source to connect to GIS data providers.'}
                      </p>
                      <button
                        onClick={() => {
                          resetForm();
                          setShowCreateModal(true);
                        }}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <i className="ri-add-line mr-2"></i>
                        Create New Data Source
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Data Source Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedDataSourceType
                    ? `${editingDataSource ? 'Edit' : 'Configure'} ${selectedDataSourceType.type_name}`
                    : `${editingDataSource ? 'Edit Data Source' : 'Choose Data Source Type'}`
                  }
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                    setEditingDataSource(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {!selectedDataSourceType ? (
                <div className="space-y-6">
                  <p className="text-gray-600 dark:text-gray-400">
                    Choose the type of data source you wish to configure
                  </p>

                  {/* Group by category */}
                  {['server', 'service', 'file', 'cloud'].map(category => {
                    const typesInCategory = dataSourceTypes.filter(type => type.category === category);
                    if (typesInCategory.length === 0) return null;

                    return (
                      <div key={category}>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                          <i className={`ri-${category === 'server' ? 'server' : category === 'service' ? 'global' : category === 'file' ? 'file' : 'cloud'}-line mr-2`}></i>
                          {category.charAt(0).toUpperCase() + category.slice(1)} Data Sources
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {typesInCategory.map((type) => (
                            <button
                              key={type.id}
                              onClick={() => handleDataSourceTypeSelect(type)}
                              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors"
                            >
                              <div className="flex items-start">
                                <i className={`${type.icon} text-xl mr-3 mt-1`} style={{ color: type.color || '#6b7280' }}></i>
                                <div>
                                  <h5 className="font-medium text-gray-900 dark:text-white">{type.type_name}</h5>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{type.description}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center">
                      <i className={`${selectedDataSourceType.icon} text-xl mr-3`} style={{ color: selectedDataSourceType.color || '#6b7280' }}></i>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">{selectedDataSourceType.type_name}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{selectedDataSourceType.description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Source Code *
                      </label>
                      <input
                        type="text"
                        value={formData.source_code}
                        onChange={(e) => setFormData({ ...formData, source_code: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., LOCAL_POSTGIS"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Source Name *
                      </label>
                      <input
                        type="text"
                        value={formData.source_name}
                        onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., Local GIS Database"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      rows={3}
                      placeholder="Enter description (optional)"
                    />
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white">Connection Configuration</h5>
                      <button
                        type="button"
                        onClick={() => setShowPasswords(!showPasswords)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                      >
                        <i className={`ri-${showPasswords ? 'eye-off' : 'eye'}-line`}></i>
                        {showPasswords ? 'Hide' : 'Show'} Passwords
                      </button>
                    </div>

                    {/* PostgreSQL/PostGIS specific fields */}
                    {(formData.source_type === 'postgresql' || formData.source_type === 'postgis') ? (
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Hostname *
                            </label>
                            <input
                              type="text"
                              value={formData.connection_config?.host || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection_config: { ...formData.connection_config, host: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                              placeholder="localhost or IP address"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Port *
                            </label>
                            <input
                              type="number"
                              value={formData.connection_config?.port || '5432'}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection_config: { ...formData.connection_config, port: parseInt(e.target.value) }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                              placeholder="5432"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Database *
                            </label>
                            <input
                              type="text"
                              value={formData.connection_config?.database || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection_config: { ...formData.connection_config, database: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                              placeholder="database name"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Schema
                            </label>
                            <input
                              type="text"
                              value={formData.connection_config?.schema || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection_config: { ...formData.connection_config, schema: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                              placeholder="public (optional)"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Username *
                            </label>
                            <input
                              type="text"
                              value={formData.connection_config?.username || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection_config: { ...formData.connection_config, username: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                              placeholder="database username"
                              required
                              autoComplete="off"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Password *
                            </label>
                            <input
                              type={showPasswords ? 'text' : 'password'}
                              value={formData.connection_config?.password || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection_config: { ...formData.connection_config, password: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                              placeholder="database password"
                              required
                              autoComplete="new-password"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Enter connection parameters as JSON:
                        </p>
                        <textarea
                          value={JSON.stringify(showPasswords ? formData.connection_config : maskPasswords(formData.connection_config), null, 2)}
                          onChange={(e) => {
                            try {
                              const config = JSON.parse(e.target.value);
                              setFormData({ ...formData, connection_config: config });
                            } catch (err) {
                              // Invalid JSON, don't update
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white font-mono text-sm"
                          rows={8}
                          placeholder={`{\n  "url": "https://example.com/service",\n  "username": "user",\n  "password": "pass"\n}`}
                        />
                      </div>
                    )}

                    {/* Test Connection Result */}
                    {testResult && (
                      <div className={`mt-3 p-3 rounded-lg border ${
                        testResult.success
                          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                          : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                      }`}>
                        <div className="flex items-start">
                          <i className={`ri-${testResult.success ? 'check-circle' : 'error-warning'}-line text-lg mr-2 ${
                            testResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}></i>
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${
                              testResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'
                            }`}>
                              {testResult.message}
                            </p>
                            {testResult.response_time_ms > 0 && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                Response time: {testResult.response_time_ms}ms
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Active
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_public"
                        checked={formData.is_public}
                        onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_public" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Public
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="cache_enabled"
                        checked={formData.cache_enabled}
                        onChange={(e) => setFormData({ ...formData, cache_enabled: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="cache_enabled" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Enable Cache
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="requires_authentication"
                        checked={formData.requires_authentication}
                        onChange={(e) => setFormData({ ...formData, requires_authentication: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="requires_authentication" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Requires Authentication
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setSelectedDataSourceType(null)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleTestConnection}
                      disabled={testingConnection}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 whitespace-nowrap disabled:opacity-50"
                    >
                      {testingConnection ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap"
                    >
                      {editingDataSource ? 'Update Data Source' : 'Create Data Source'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mr-3">
                <i className="ri-alert-line text-red-600 dark:text-red-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Data Source</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this data source? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 whitespace-nowrap"
              >
                Delete Source
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataStores;
